import { useMemo } from "react";
import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { createKeyMap } from "@tellescope/utils";

type Assignment = TellescopeModelFromName<"managed_content_record_assignments">;
type ContentRecord = TellescopeModelFromName<"managed_content_records">;

export interface AssignedContent {
  content: ContentRecord;
  assignment: Assignment;
}

export interface UseContentRecordsResult {
  assignedContent: AssignedContent[];
  isLoading: boolean;
  fetchNextPage: () => void;
}

/**
 * The content assigned to a contact, paired with the assignment that granted it.
 *
 * The records are fetched in a single `getByIds` batch rather than one request per id, so loading a
 * page of assignments costs one request instead of one per record. A new page does widen the id list
 * (and with it the query key), but the query layer's cache sharing resolves the already-loaded ids
 * from their seeded individual caches and sends only the missing ones, so the refetch does not scale
 * with the number of records already displayed.
 *
 * @example
 * const { assignedContent, isLoading, fetchNextPage } = useContentRecords(contactId);
 */
export const useContentRecords = (contactId: string): UseContentRecordsResult => {
  const assignmentsQuery = useTellescopeInfiniteQuery({
    model: "managed_content_record_assignments",
    method: "getSome",
    request: {
      filter: {
        enduserId: contactId,
      },
    },
    // Keyed by contentId so a record loaded on an earlier page is not requested twice when a later
    // page repeats it, and so the order below follows the assignment sequence.
    select: (data) => createKeyMap(data.pages.flat(), "contentId"),
  });

  const assignmentsByContentId = assignmentsQuery.data;
  const contentIds = useMemo(
    () => (assignmentsByContentId ? Object.keys(assignmentsByContentId) : []),
    [assignmentsByContentId]
  );

  const contentQuery = useTellescopeQuery({
    model: "managed_content_records",
    method: "getByIds",
    request: { ids: contentIds },
    enabled: contentIds.length > 0,
    select: (data) => createKeyMap(data.matches, "id"),
    placeholderData: (previous) => previous,
  });

  const contentById = contentQuery.data;
  const assignedContent = contentIds.reduce<AssignedContent[]>((acc, contentId) => {
    const content = contentById?.[contentId];
    const assignment = assignmentsByContentId?.[contentId];
    if (content && assignment) acc.push({ content, assignment });

    return acc;
  }, []);

  return {
    assignedContent,
    isLoading:
      assignedContent.length === 0 && (assignmentsQuery.isLoading || (contentIds.length > 0 && contentQuery.isLoading)),
    fetchNextPage: () => void assignmentsQuery.fetchNextPage(),
  };
};
